import { useMemo } from "react";
import type {
  SessionDataContextValue,
  SessionLiveContextValue,
} from "./context";

type SessionDataActions = SessionDataContextValue["actions"];
type SessionNavigationActions = Pick<
  SessionDataActions,
  "focusProduct" | "goBack" | "goBackN" | "navigateToProduct" | "selectVersion"
>;
type SessionChatActions = Pick<
  SessionDataActions,
  | "clearChatTargets"
  | "clearNewMessageIds"
  | "loadOlderMessages"
  | "refreshChat"
  | "refreshCollabs"
  | "setChatIndicatedMessage"
  | "setChatMode"
  | "setChatTargetMessage"
  | "setChatTargetVersion"
>;
type SessionReadActions = Pick<
  SessionDataActions,
  | "addOwnMessageIds"
  | "markAllChatAsRead"
  | "markAllCollabsAsRead"
  | "markChatReadUpTo"
  | "markCollabsReadUpTo"
  | "markProductRead"
>;
type SessionProductActions = Pick<
  SessionDataActions,
  "ensureProductCollabs" | "ensureProductVersions" | "refreshProducts"
>;
type SessionCacheActions = Pick<
  SessionDataActions,
  "cacheProductDetail" | "cacheVersionDetail"
>;
type SessionRefreshActions = Pick<
  SessionDataActions,
  "applySessionLocalPatch" | "refreshMembers" | "refreshSession"
>;
type SessionLiveActions = Pick<
  SessionDataActions,
  | "flagLiveFlash"
  | "markProductDetailObserved"
  | "markReflectionsObserved"
  | "pushMemberActivity"
>;
type SessionBasicsValue = Pick<
  SessionDataContextValue,
  | "actions"
  | "loading"
  | "notFound"
  | "session"
  | "sessionId"
  | "teamDefaultStartingRounds"
  | "teamDetails"
>;
type SessionEntityValue = Pick<
  SessionDataContextValue,
  | "collabPreviews"
  | "fromShortId"
  | "attachedFiles"
  | "members"
  | "productDetailsById"
  | "products"
  | "reflectionPreviews"
  | "toShortId"
  | "versionDetailsById"
  | "versionPreviews"
  | "versions"
>;
type SessionConversationValue = Pick<
  SessionDataContextValue,
  | "chat"
  | "chatCursor"
  | "chatHasMore"
  | "chatLoadingMore"
  | "chatMessageIds"
  | "chatMessages"
  | "collabs"
  | "collabsByProduct"
>;
type SessionReadValue = Pick<
  SessionDataContextValue,
  | "isCollabUnread"
  | "mainChatScopeKey"
  | "productReads"
  | "productsSeen"
  | "unread"
  | "unreadCollabCounts"
>;

function useSessionNavigationActions(
  actions: SessionDataActions,
): SessionNavigationActions {
  const { focusProduct, goBack, goBackN, navigateToProduct, selectVersion } =
    actions;

  return useMemo(
    () => ({ focusProduct, navigateToProduct, goBack, goBackN, selectVersion }),
    [focusProduct, goBack, goBackN, navigateToProduct, selectVersion],
  );
}

function useSessionChatActions(actions: SessionDataActions): SessionChatActions {
  const {
    clearChatTargets,
    clearNewMessageIds,
    loadOlderMessages,
    refreshChat,
    refreshCollabs,
    setChatIndicatedMessage,
    setChatMode,
    setChatTargetMessage,
    setChatTargetVersion,
  } = actions;

  return useMemo(
    () => ({
      setChatMode,
      setChatTargetVersion,
      setChatTargetMessage,
      setChatIndicatedMessage,
      clearChatTargets,
      clearNewMessageIds,
      loadOlderMessages,
      refreshChat,
      refreshCollabs,
    }),
    [
      clearChatTargets,
      clearNewMessageIds,
      loadOlderMessages,
      refreshChat,
      refreshCollabs,
      setChatIndicatedMessage,
      setChatMode,
      setChatTargetMessage,
      setChatTargetVersion,
    ],
  );
}

function useSessionReadActions(actions: SessionDataActions): SessionReadActions {
  const {
    addOwnMessageIds,
    markAllChatAsRead,
    markAllCollabsAsRead,
    markChatReadUpTo,
    markCollabsReadUpTo,
    markProductRead,
  } = actions;

  return useMemo(
    () => ({
      markProductRead,
      markAllChatAsRead,
      markAllCollabsAsRead,
      markChatReadUpTo,
      markCollabsReadUpTo,
      addOwnMessageIds,
    }),
    [
      addOwnMessageIds,
      markAllChatAsRead,
      markAllCollabsAsRead,
      markChatReadUpTo,
      markCollabsReadUpTo,
      markProductRead,
    ],
  );
}

function useSessionProductActions(
  actions: SessionDataActions,
): SessionProductActions {
  const { ensureProductCollabs, ensureProductVersions, refreshProducts } =
    actions;

  return useMemo(
    () => ({ refreshProducts, ensureProductVersions, ensureProductCollabs }),
    [ensureProductCollabs, ensureProductVersions, refreshProducts],
  );
}

function useSessionCacheActions(
  actions: SessionDataActions,
): SessionCacheActions {
  const { cacheProductDetail, cacheVersionDetail } = actions;

  return useMemo(
    () => ({ cacheProductDetail, cacheVersionDetail }),
    [cacheProductDetail, cacheVersionDetail],
  );
}

function useSessionRefreshActions(
  actions: SessionDataActions,
): SessionRefreshActions {
  const { applySessionLocalPatch, refreshMembers, refreshSession } = actions;

  return useMemo(
    () => ({ refreshMembers, refreshSession, applySessionLocalPatch }),
    [applySessionLocalPatch, refreshMembers, refreshSession],
  );
}

function useSessionLiveActions(actions: SessionDataActions): SessionLiveActions {
  const {
    flagLiveFlash,
    markProductDetailObserved,
    markReflectionsObserved,
    pushMemberActivity,
  } = actions;

  return useMemo(
    () => ({
      flagLiveFlash,
      markReflectionsObserved,
      markProductDetailObserved,
      pushMemberActivity,
    }),
    [
      flagLiveFlash,
      markProductDetailObserved,
      markReflectionsObserved,
      pushMemberActivity,
    ],
  );
}

export function useSessionDataActions(
  actions: SessionDataActions,
): SessionDataActions {
  const navigationActions = useSessionNavigationActions(actions);
  const chatActions = useSessionChatActions(actions);
  const readActions = useSessionReadActions(actions);
  const productActions = useSessionProductActions(actions);
  const cacheActions = useSessionCacheActions(actions);
  const refreshActions = useSessionRefreshActions(actions);
  const liveActions = useSessionLiveActions(actions);

  return useMemo(
    () => ({
      ...navigationActions,
      ...chatActions,
      ...readActions,
      ...productActions,
      ...cacheActions,
      ...refreshActions,
      ...liveActions,
    }),
    [
      cacheActions,
      chatActions,
      liveActions,
      navigationActions,
      productActions,
      readActions,
      refreshActions,
    ],
  );
}

function useSessionBasicsValue(
  input: SessionDataContextValue,
): SessionBasicsValue {
  const {
    actions,
    loading,
    notFound,
    session,
    sessionId,
    teamDefaultStartingRounds,
    teamDetails,
  } = input;

  return useMemo(
    () => ({
      sessionId,
      session,
      teamDefaultStartingRounds,
      teamDetails,
      loading,
      notFound,
      actions,
    }),
    [
      actions,
      loading,
      notFound,
      session,
      sessionId,
      teamDefaultStartingRounds,
      teamDetails,
    ],
  );
}

function useSessionEntityValue(
  input: SessionDataContextValue,
): SessionEntityValue {
  const {
    attachedFiles,
    collabPreviews,
    fromShortId,
    members,
    productDetailsById,
    products,
    reflectionPreviews,
    toShortId,
    versionDetailsById,
    versionPreviews,
    versions,
  } = input;

  return useMemo(
    () => ({
      products,
      versions,
      productDetailsById,
      versionDetailsById,
      members,
      attachedFiles,
      versionPreviews,
      collabPreviews,
      reflectionPreviews,
      fromShortId,
      toShortId,
    }),
    [
      collabPreviews,
      attachedFiles,
      fromShortId,
      members,
      productDetailsById,
      products,
      reflectionPreviews,
      toShortId,
      versionDetailsById,
      versionPreviews,
      versions,
    ],
  );
}

function useSessionConversationValue(
  input: SessionDataContextValue,
): SessionConversationValue {
  const {
    chat,
    chatCursor,
    chatHasMore,
    chatLoadingMore,
    chatMessageIds,
    chatMessages,
    collabs,
    collabsByProduct,
  } = input;

  return useMemo(
    () => ({
      chatMessages,
      collabs,
      chatMessageIds,
      chatCursor,
      chatHasMore,
      chatLoadingMore,
      collabsByProduct,
      chat,
    }),
    [
      chat,
      chatCursor,
      chatHasMore,
      chatLoadingMore,
      chatMessageIds,
      chatMessages,
      collabs,
      collabsByProduct,
    ],
  );
}

function useSessionReadValue(input: SessionDataContextValue): SessionReadValue {
  const {
    isCollabUnread,
    mainChatScopeKey,
    productReads,
    productsSeen,
    unread,
    unreadCollabCounts,
  } = input;

  return useMemo(
    () => ({
      unread,
      productReads,
      unreadCollabCounts,
      productsSeen,
      mainChatScopeKey,
      isCollabUnread,
    }),
    [
      isCollabUnread,
      mainChatScopeKey,
      productReads,
      productsSeen,
      unread,
      unreadCollabCounts,
    ],
  );
}

export function useSessionDataValue(
  input: SessionDataContextValue,
): SessionDataContextValue {
  const basicsValue = useSessionBasicsValue(input);
  const entityValue = useSessionEntityValue(input);
  const conversationValue = useSessionConversationValue(input);
  const readValue = useSessionReadValue(input);
  const navigation = input.navigation;

  return useMemo(
    () => ({
      ...basicsValue,
      ...entityValue,
      ...conversationValue,
      navigation,
      ...readValue,
    }),
    [basicsValue, conversationValue, entityValue, navigation, readValue],
  );
}

export function useSessionLiveValue({
  newMessageIds,
  liveFlashes,
  memberActivity,
}: SessionLiveContextValue): SessionLiveContextValue {
  return useMemo(
    () => ({ newMessageIds, liveFlashes, memberActivity }),
    [newMessageIds, liveFlashes, memberActivity],
  );
}
